import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    clearAttraction,
    clearDriversLicence,
    clearEthnicity,
    clearFirstLanguage,
    clearHousing,
    clearMaritalStatus,
    loadAttractionDetails,
    loadDriversLicenceDetails,
    loadEthnicityDetails,
    loadFirstLanguageDetails,
    loadHousingDetails,
    loadMaritalStatusDetails
} from '../../../store/directusService';
import { loadContractConstants, updateContractConstants } from '../../../store/participantService';
import {
    updateDirectusContractSelections,
    updatePreSelects
} from '../../../utils/directusFunctions';
import {
    NO_CONTRACT_ERROR,
    NO_UPDATE_ERROR
} from '../../../utils/formValidation/commonErrorConstants';
import {
    ATTRACTION_DETAILS_ERROR,
    CONTRACT_CONSTANTS_ERROR,
    ETHNICITY_DETAILS_ERROR,
    HOUSING_DETAILS_ERROR,
    LANGUAGE_DETAILS_ERROR,
    LICENCE_DETAILS_ERROR,
    MARITAL_STATUS_DETAILS_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import { clearKeys } from '../../../utils/objectUtils';
import { hasRole, LOCAL_ADMIN, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import MultiSelect from '../../formElements/MultiSelect';
import RadioButtons from '../../formElements/RadioButtons';
import NotFound from '../../notFound/NotFound';
import LoadingSpinner from '../../ui/LoadingSpinner';

import { validate } from './validateParticipantInformationAdmin';

import form from '../../../commonStyles/formStyles.module.css';

const ethnicityName = 'ethnicity';
const firstLanguageName = 'first_language';
const driversLicenceName = 'drivers_licence';
const maritalStatusName = 'marital_status';
const housingName = 'housing';
const attractionName = 'attraction';

const submitMessages = {
    ethnicities: 'Ethnicities have been updated',
    firstLanguages: 'First languages have been updated',
    driversLicences: 'Drivers licences have been updated',
    maritalStatuses: 'Marital statuses have been updated',
    housing: 'Housing details have been updated',
    attractions: 'Attraction details have been updated',
    expandPoNumberAccessPermissions:
        'Contract constants: PO number access permissions have been updated'
};

const acceptedRoles = [SUPERUSER];
const viewRoles = [LOCAL_ADMIN, SUPERUSER];

const ParticipantInformationAdmin = ({ contractId }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        selectedEthnicities: [],
        preSelectedEthnicityIds: [],
        preSelectedEthnicities: [],

        selectedFirstLanguages: [],
        preSelectedFirstLanguageIds: [],
        preSelectedFirstLanguages: [],

        selectedDriversLicences: [],
        preSelectedDriversLicenceIds: [],
        preSelectedDriversLicences: [],

        selectedMaritalStatuses: [],
        preSelectedMaritalStatusIds: [],
        preSelectedMaritalStatuses: [],

        selectedHousing: [],
        preSelectedHousingIds: [],
        preSelectedHousing: [],

        selectedAttractions: [],
        preSelectedAttractionIds: [],
        preSelectedAttractions: [],

        expandPoNumberAccessPermissions: false
    };

    const [admin, setAdmin] = useState(initialState);
    const [keys, setKeys] = useState({
        ethnicities: '0',
        firstLanguages: '1',
        driversLicences: '2',
        maritalStatuses: '3',
        housing: '4',
        attractions: '5',
        attractionDetails: '6'
    });
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const ethnicityDetails = useSelector(
        (state) => state.entities.directusService.ethnicityDetails
    );
    const firstLanguageDetails = useSelector(
        (state) => state.entities.directusService.firstLanguageDetails
    );
    const driversLicenceDetails = useSelector(
        (state) => state.entities.directusService.driversLicenceDetails
    );
    const maritalStatusDetails = useSelector(
        (state) => state.entities.directusService.maritalStatusDetails
    );
    const housingDetails = useSelector((state) => state.entities.directusService.housingDetails);
    const attractionDetails = useSelector(
        (state) => state.entities.directusService.attractionDetails
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const errorMessage = useSelector((state) => state.entities.formsState.errorMessage);

    // USEEFFECTS
    useEffect(() => {
        if (contractId) {
            setAdmin(initialState);
            setKeys(clearKeys(keys));
            dispatch(clearEthnicity());
            dispatch(clearFirstLanguage());
            dispatch(clearDriversLicence());
            dispatch(clearMaritalStatus());
            dispatch(clearHousing());
            dispatch(clearAttraction());
            dispatch(loadContractConstants(contractId));
            dispatch(loadEthnicityDetails());
            dispatch(loadFirstLanguageDetails());
            dispatch(loadDriversLicenceDetails());
            dispatch(loadMaritalStatusDetails());
            dispatch(loadHousingDetails());
            dispatch(loadAttractionDetails());
        }
    }, [contractId]);

    useEffect(() => {
        if ('expandPoNumberAccessPermissions' in contractConstants)
            setAdmin((admin) => ({
                ...admin,
                expandPoNumberAccessPermissions: contractConstants.expandPoNumberAccessPermissions
            }));
    }, [contractConstants]);

    useEffect(() => {
        if (ethnicityDetails?.length > 0) {
            const { preSelects, preSelectIds } = updatePreSelects(
                ethnicityName,
                ethnicityDetails,
                contractId
            );
            setAdmin((admin) => ({
                ...admin,
                preSelectedEthnicities: preSelects,
                preSelectedEthnicityIds: preSelectIds
            }));
        }
    }, [ethnicityDetails]);

    useEffect(() => {
        if (firstLanguageDetails?.length) {
            const { preSelects, preSelectIds } = updatePreSelects(
                firstLanguageName,
                firstLanguageDetails,
                contractId
            );
            setAdmin((admin) => ({
                ...admin,
                preSelectedFirstLanguages: preSelects,
                preSelectedFirstLanguageIds: preSelectIds
            }));
        }
    }, [firstLanguageDetails]);

    useEffect(() => {
        if (driversLicenceDetails?.length) {
            const { preSelects, preSelectIds } = updatePreSelects(
                driversLicenceName,
                driversLicenceDetails,
                contractId
            );
            setAdmin((admin) => ({
                ...admin,
                preSelectedDriversLicences: preSelects,
                preSelectedDriversLicenceIds: preSelectIds
            }));
        }
    }, [driversLicenceDetails]);

    useEffect(() => {
        if (maritalStatusDetails?.length) {
            const { preSelects, preSelectIds } = updatePreSelects(
                maritalStatusName,
                maritalStatusDetails,
                contractId
            );
            setAdmin((admin) => ({
                ...admin,
                preSelectedMaritalStatuses: preSelects,
                preSelectedMaritalStatusIds: preSelectIds
            }));
        }
    }, [maritalStatusDetails]);

    useEffect(() => {
        if (housingDetails?.length) {
            const { preSelects, preSelectIds } = updatePreSelects(
                housingName,
                housingDetails,
                contractId
            );
            setAdmin((admin) => ({
                ...admin,
                preSelectedHousing: preSelects,
                preSelectedHousingIds: preSelectIds
            }));
        }
    }, [housingDetails]);

    useEffect(() => {
        if (attractionDetails?.length) {
            const { preSelects, preSelectIds } = updatePreSelects(
                attractionName,
                attractionDetails,
                contractId
            );
            setAdmin((admin) => ({
                ...admin,
                preSelectedAttractions: preSelects,
                preSelectedAttractionIds: preSelectIds
            }));
        }
    }, [attractionDetails]);

    useEffect(() => {
        if (!isSubmitting) setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage.includes(submitMessages.ethnicities)) {
            setAdmin((prev) => ({
                ...prev,
                selectedEthnicities: [],
                preSelectedEthnicities: [],
                preSelectedEthnicityIds: []
            }));
            dispatch(loadEthnicityDetails());
        }
        if (successMessage.includes(submitMessages.firstLanguages)) {
            setAdmin((prev) => ({
                ...prev,
                selectedFirstLanguages: [],
                preSelectedFirstLanguages: [],
                preSelectedFirstLanguageIds: []
            }));
            dispatch(loadFirstLanguageDetails());
        }
        if (successMessage.includes(submitMessages.driversLicences)) {
            setAdmin((prev) => ({
                ...prev,
                selectedDriversLicences: [],
                preSelectedDriversLicences: [],
                preSelectedDriversLicenceIds: []
            }));
            dispatch(loadDriversLicenceDetails());
        }
        if (successMessage.includes(submitMessages.maritalStatuses)) {
            setAdmin((prev) => ({
                ...prev,
                selectedMaritalStatuses: [],
                preSelectedMaritalStatuses: [],
                preSelectedMaritalStatusIds: []
            }));
            dispatch(loadMaritalStatusDetails());
        }
        if (successMessage.includes(submitMessages.housing)) {
            setAdmin((prev) => ({
                ...prev,
                selectedHousing: [],
                preSelectedHousing: [],
                preSelectedHousingIds: []
            }));
            dispatch(loadHousingDetails());
        }
        if (successMessage.includes(submitMessages.attractions)) {
            setAdmin((prev) => ({
                ...prev,
                selectedAttractions: [],
                preSelectedAttractions: [],
                preSelectedAttractionIds: []
            }));
            dispatch(loadAttractionDetails());
        }
        isButtonDisabled && setIsButtonDisabled(false);
        isSubmitting && setIsSubmitting(false);
    }, [successMessage, errorMessage]);

    // HELPER FNS
    const loadingError = () => {
        if (!contractId) return NO_CONTRACT_ERROR;
        if (ethnicityDetails?.length < 1) return ETHNICITY_DETAILS_ERROR;
        if (firstLanguageDetails?.length < 1) return LANGUAGE_DETAILS_ERROR;
        if (driversLicenceDetails?.length < 1) return LICENCE_DETAILS_ERROR;
        if (maritalStatusDetails?.length < 1) return MARITAL_STATUS_DETAILS_ERROR;
        if (housingDetails?.length < 1) return HOUSING_DETAILS_ERROR;
        if (attractionDetails?.length < 1) return ATTRACTION_DETAILS_ERROR;
        if (Object.keys(contractConstants).length < 1) return CONTRACT_CONSTANTS_ERROR;
    };

    const clearError = (key) => {
        if (key in errors) setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value, error) => {
        clearError(error);
        setAdmin((admin) => ({ ...admin, [key]: value }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        let isUpdated = false;
        const newErrors = validate(admin);
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;
        const isUpdatedEthnicities = updateDirectusContractSelections(
            ethnicityName,
            admin.preSelectedEthnicityIds,
            admin.preSelectedEthnicities,
            admin.selectedEthnicities,
            contractId,
            submitMessages.ethnicities,
            dispatch
        );

        const isUpdatedFirstLanguages = updateDirectusContractSelections(
            firstLanguageName,
            admin.preSelectedFirstLanguageIds,
            admin.preSelectedFirstLanguages,
            admin.selectedFirstLanguages,
            contractId,
            submitMessages.firstLanguages,
            dispatch
        );

        const isUpdatedDriversLicences = updateDirectusContractSelections(
            driversLicenceName,
            admin.preSelectedDriversLicenceIds,
            admin.preSelectedDriversLicences,
            admin.selectedDriversLicences,
            contractId,
            submitMessages.driversLicences,
            dispatch
        );

        const isUpdatedMaritalStatuses = updateDirectusContractSelections(
            maritalStatusName,
            admin.preSelectedMaritalStatusIds,
            admin.preSelectedMaritalStatuses,
            admin.selectedMaritalStatuses,
            contractId,
            submitMessages.maritalStatuses,
            dispatch
        );

        const isUpdatedHousing = updateDirectusContractSelections(
            housingName,
            admin.preSelectedHousingIds,
            admin.preSelectedHousing,
            admin.selectedHousing,
            contractId,
            submitMessages.housing,
            dispatch
        );

        const isUpdatedAttractions = updateDirectusContractSelections(
            attractionName,
            admin.preSelectedAttractionIds,
            admin.preSelectedAttractions,
            admin.selectedAttractions,
            contractId,
            submitMessages.attractions,
            dispatch
        );

        if (
            isUpdatedEthnicities ||
            isUpdatedFirstLanguages ||
            isUpdatedDriversLicences ||
            isUpdatedMaritalStatuses ||
            isUpdatedHousing ||
            isUpdatedAttractions
        ) {
            isUpdated = true;
        }
        if (
            admin.expandPoNumberAccessPermissions !==
            contractConstants.expandPoNumberAccessPermissions
        ) {
            dispatch(
                updateContractConstants(
                    {
                        ...contractConstants,
                        expandPoNumberAccessPermissions: admin.expandPoNumberAccessPermissions
                    },
                    submitMessages.expandPoNumberAccessPermissions
                )
            );
            isUpdated = true;
        }

        isUpdated
            ? setIsSubmitting(true)
            : setErrors((prev) => ({ ...prev, button: { error: true, message: NO_UPDATE_ERROR } }));
    };

    // RENDER
    if (!hasRole(viewRoles, roles)) return <NotFound />;
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <form
            onSubmit={onSubmit}
            className={form.formWrapper}
            data-testid="form_start_participantInformationAdmin">
            <div className={form.formSection}>
                <div className={form.formColumn}>
                    <MultiSelect
                        id="ethnicities"
                        key={keys.ethnicities}
                        label="Ethnicities"
                        placeholder="Select ethnicities..."
                        disabled={!hasRole(acceptedRoles, roles)}
                        mandatory={true}
                        menuItems={ethnicityDetails || []}
                        preSelectedIds={admin.preSelectedEthnicityIds}
                        preSelects={admin.preSelectedEthnicities}
                        error={errors.ethnicities}
                        onChange={(chosenIds) =>
                            onUpdate('selectedEthnicities', chosenIds, 'ethnicities')
                        }
                    />
                    <MultiSelect
                        id="firstLanguages"
                        key={keys.firstLanguages}
                        label="First Languages"
                        placeholder="Select first languages..."
                        disabled={!hasRole(acceptedRoles, roles)}
                        mandatory={true}
                        menuItems={firstLanguageDetails || []}
                        preSelectedIds={admin.preSelectedFirstLanguageIds}
                        preSelects={admin.preSelectedFirstLanguages}
                        error={errors.firstLanguages}
                        onChange={(chosenIds) =>
                            onUpdate('selectedFirstLanguages', chosenIds, 'firstLanguages')
                        }
                    />
                    <MultiSelect
                        id="driversLicences"
                        key={keys.driversLicences}
                        label="Drivers Licences"
                        placeholder="Select drivers licence types..."
                        disabled={!hasRole(acceptedRoles, roles)}
                        mandatory={true}
                        menuItems={driversLicenceDetails || []}
                        preSelectedIds={admin.preSelectedDriversLicenceIds}
                        preSelects={admin.preSelectedDriversLicences}
                        error={errors.driversLicences}
                        onChange={(chosenIds) =>
                            onUpdate('selectedDriversLicences', chosenIds, 'driversLicences')
                        }
                    />

                    <RadioButtons
                        id="expandPoNumberAccessPermissions"
                        label="Expand PO Number Access Permissions"
                        disabled={!hasRole(acceptedRoles, roles)}
                        value={admin.expandPoNumberAccessPermissions || false}
                        onChange={(option) => onUpdate('expandPoNumberAccessPermissions', option)}
                    />
                </div>

                <div className={form.formColumn}>
                    <MultiSelect
                        id="maritalStatuses"
                        key={keys.maritalStatuses}
                        label="Marital Statuses"
                        placeholder="Select marital statuses..."
                        disabled={!hasRole(acceptedRoles, roles)}
                        mandatory={true}
                        menuItems={maritalStatusDetails || []}
                        preSelectedIds={admin.preSelectedMaritalStatusIds}
                        preSelects={admin.preSelectedMaritalStatuses}
                        error={errors.maritalStatuses}
                        onChange={(chosenIds) =>
                            onUpdate('selectedMaritalStatuses', chosenIds, 'maritalStatuses')
                        }
                    />

                    <MultiSelect
                        id="housing"
                        key={keys.housing}
                        label="Housing"
                        placeholder="Select housing types..."
                        disabled={!hasRole(acceptedRoles, roles)}
                        mandatory={true}
                        menuItems={housingDetails || []}
                        preSelectedIds={admin.preSelectedHousingIds}
                        preSelects={admin.preSelectedHousing}
                        error={errors.housing}
                        onChange={(chosenIds) => onUpdate('selectedHousing', chosenIds, 'housing')}
                    />
                    <MultiSelect
                        id="attractions"
                        key={keys.attractions}
                        label="Attractions"
                        placeholder="Select attraction details..."
                        disabled={!hasRole(acceptedRoles, roles)}
                        mandatory={true}
                        menuItems={attractionDetails || []}
                        preSelectedIds={admin.preSelectedAttractionIds}
                        preSelects={admin.preSelectedAttractions}
                        error={errors.attractions}
                        onChange={(chosenIds) =>
                            onUpdate('selectedAttractions', chosenIds, 'attractions')
                        }
                    />
                </div>
            </div>
            <div className={form.formSection}>
                <div className={form.formColumn}>
                    <p>Contract</p>
                    <p>Service</p>
                    <p>Access to own transport</p>
                    <p>Access to Public transport </p>
                    <p>Hobbies/Interests</p>
                    <p>Dependants</p>
                    <p>Add a Dependant</p>
                </div>
            </div>

            <Button
                id="updateParticipantInformationButton"
                content="UPDATE PARTICIPANT INFORMATION"
                disabled={!hasRole(acceptedRoles, roles) || isButtonDisabled}
                error={errors.button}
                clearError={() => clearError('button')}
            />
        </form>
    );
};

ParticipantInformationAdmin.propTypes = {
    contractId: PropTypes.string
};

export default ParticipantInformationAdmin;
